import {
  addMinutes,
  format as fnsFormat,
  parseISO,
  isValid,
  roundToNearestMinutes,
  addMonths,
  getMinutes,
  differenceInMilliseconds,
} from 'date-fns';

import {
  utcToZonedTime,
  zonedTimeToUtc,
  toDate,
  format as formatTz,
  formatInTimeZone,
} from 'date-fns-tz';

export const formats = Object.freeze({
  ETC_TIMEZONE: 'Etc/UTC',
  FORMAT_TO_ISO: "yyyy-MM-dd'T'HH:mm:ss'.000Z'",
});

export const durationToHuman = (duration) => {
  const fnDuration = new Date(duration * 1000);
  const fnformatted = format(
    addMinutes(fnDuration, fnDuration.getTimezoneOffset()),
    'HH:mm:ss'
  );

  return fnformatted;
};

export const isValidISODate = (value) =>
  typeof value === 'string' && isValid(parseISO(value));

export const format = (date, format, parse) => {
  if (
    !date ||
    !format ||
    ((parse || typeof date === 'string') && !isValidISODate(date)) ||
    (typeof date === 'object' && !isValid(date))
  ) {
    return '';
  }

  if (typeof date === 'string') {
    return fnsFormat(parseISO(date), format);
  }

  if (parse) return fnsFormat(parseISO(date), format);

  return fnsFormat(date, format);
};

export const utcToTimezone = (date, timezone) => utcToZonedTime(date, timezone);

export const getISOStringFromStringDate = (date = '') => {
  return new Date(date).toISOString();
};

const getDefaultDates = () => {
  const todayRaw = new Date();

  const today = new Date(todayRaw.valueOf());
  const oneMonthAgo = addMonths(today, -1);

  const endDate = today.toISOString();
  const startDate = oneMonthAgo.toISOString();

  return { startDate, endDate };
};

const PLAIN_DATE = /^\d{4}-\d{2}-\d{2}$/;

// The API filters by UTC day, so the user's local day is widened to every UTC
// day it overlaps: results are never dropped, at most |offset| hours are added.
export const formatDateToSubmit = (date = '', timeZone, boundary = 'start') => {
  if (!date) return undefined;
  if (!isValid(new Date(date))) return undefined;
  if (!timeZone) return getISOStringFromStringDate(date).split('T')[0];

  const localDay =
    typeof date === 'string' && PLAIN_DATE.test(date)
      ? date
      : formatInTimeZone(date, timeZone, defaultDateFormat);
  const time = boundary === 'end' ? '23:59:59.999' : '00:00:00';
  return zonedTimeToUtc(`${localDay}T${time}`, timeZone)
    .toISOString()
    .split('T')[0];
};

export const getDefaultStartDate = () => {
  const { startDate } = getDefaultDates();
  return startDate.substring(0, 10);
};

export const getDefaultEndDate = () => {
  const { endDate } = getDefaultDates();
  return endDate.substring(0, 10);
};

export const getYearAndMonthFromDate = (date) => {
  if (!date || typeof date !== 'string') return null;
  const splittedDate = date.split('-');
  if (splittedDate.length < 2) return null;
  return `${splittedDate[0]}-${splittedDate[1]}`;
};

export const convertUTCToTimezone = (date, timezone) => {
  try {
    if (!date || !timezone) return null;

    const utcDate =
      typeof date === 'string' ? toDate(date, { timeZone: timezone }) : date;

    if ((typeof date !== 'string' && !isValid(date)) || !isValid(utcDate))
      return null;

    return utcToZonedTime(utcDate, timezone);
  } catch (error) {
    return null;
  }
};

export const convertUTCDateToUserTimezone = (date, timezone) => {
  try {
    if (!date || !timezone) return null;

    let dateObj;

    if (typeof date === 'string') {
      dateObj = new Date(date);
      if (!isValid(dateObj)) return null;
    } else if (date instanceof Date) {
      if (!isValid(date)) return null;
      dateObj = date;
    } else {
      return null;
    }

    return utcToZonedTime(dateObj, timezone);
  } catch (error) {
    console.error('Error in convertUTCDateToUserTimezone:', error);
    return null;
  }
};

export const getGMTTimeFromTimezoneCodeInTimezone = (timezone) => {
  const date = new Date();
  const pad = (n) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  let tzString;

  if (!timezone || timezone === '') {
    return '+00:00';
  }

  if (timezone === 'UTC' || timezone === 'Etc/UTC' || timezone === 'Etc/GMT') {
    tzString = 'Z';
  } else {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });

      const formatted = formatter.format(date);
      const offsetMatch = formatted.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);

      if (offsetMatch) {
        const sign = offsetMatch[1];
        const hours = pad(parseInt(offsetMatch[2]));
        const minutes = offsetMatch[3] ? offsetMatch[3] : '00';
        tzString = `${sign}${hours}:${minutes}:00`;
      } else {
        const utcDate = new Date(
          date.toLocaleString('en-US', { timeZone: 'UTC' })
        );
        const tzDate = new Date(
          date.toLocaleString('en-US', { timeZone: timezone })
        );

        const offsetMs = utcDate.getTime() - tzDate.getTime();
        const offsetMin = offsetMs / 60000;

        if (offsetMin === 0) {
          tzString = 'Z';
        } else {
          const absOffset = Math.abs(offsetMin);
          const hours = pad(Math.floor(absOffset / 60));
          const minutes = pad(Math.floor(absOffset % 60));

          tzString = `${offsetMin >= 0 ? '-' : '+'}${hours}:${minutes}:00`;
        }
      }
    } catch (error) {
      try {
        // Second fallback using a different approach
        const utcDate = new Date(
          date.toLocaleString('en-US', { timeZone: 'UTC' })
        );
        const tzDate = new Date(
          date.toLocaleString('en-US', { timeZone: timezone })
        );

        const offsetMs = utcDate.getTime() - tzDate.getTime();
        const offsetMin = offsetMs / 60000;

        if (offsetMin === 0) {
          tzString = 'Z';
        } else {
          const absOffset = Math.abs(offsetMin);
          const hours = pad(Math.floor(absOffset / 60));
          const minutes = pad(Math.floor(absOffset % 60));

          tzString = `${offsetMin >= 0 ? '-' : '+'}${hours}:${minutes}:00`;
        }
      } catch (fallbackError) {
        tzString = '+00:00';
      }
    }
  }
  return tzString;
};

// A date-only value means the start of that day in `timezone`.
export const convertUserTimezoneToUTC = (date, timezone, dateOnly = false) => {
  const [day, time] = format(date, 'yyyy-MM-dd HH:mm:ss').split(' ');
  const zoned = `${day}T${dateOnly ? '00:00:00' : time}`;
  return zonedTimeToUtc(zoned, timezone).toISOString();
};

export const addMinutesUTC = (date, minutes) => {
  if (!date) return '';
  const parse = addMinutes(
    toDate(date, { timeZone: formats.ETC_TIMEZONE }),
    minutes
  );
  const response = formatTz(
    utcToZonedTime(parse, formats.ETC_TIMEZONE),
    formats.FORMAT_TO_ISO
  );
  return response;
};

export const roundTimeUTC = (date, minutes = 30) => {
  if (!date) return '';
  const parse = roundToNearestMinutes(
    toDate(date, { timeZone: formats.ETC_TIMEZONE }),
    {
      nearestTo: minutes,
      roundingMethod: 'ceil',
    }
  );
  const response = formatTz(
    utcToZonedTime(parse, formats.ETC_TIMEZONE),
    formats.FORMAT_TO_ISO
  );
  return response;
};

export const formatDatetUTCToTimezone = (
  date,
  userDateFormat,
  userTimeFormat,
  timeZone
) => {
  if (!date) return '';
  let format = '';
  format += userDateFormat ? `${userDateFormat}` : '';
  format += userDateFormat && userTimeFormat ? ' ' : '';
  format += userTimeFormat ? ` ${userTimeFormat}` : '';
  const dateInTimezone = utcToTimezone(date, timeZone);
  if (!isValid(dateInTimezone)) return '';
  return formatTz(dateInTimezone, format);
};

export const getUTCDefaultStartDate = () => {
  const { startDate } = getDefaultDates();
  return startDate;
};

export const getUTCCustomStartDate = (days) => {
  const currentDate = new Date();
  days && currentDate.setDate(currentDate.getDate() - days);
  return formatToSimpleDate(currentDate);
};

export const getUTCDefaultEndDate = () => {
  const { endDate } = getDefaultDates();
  return endDate;
};

export const getUTCLast7Days = () => {
  const current_date = new Date();
  const end_date = formatToSimpleDate(current_date);

  current_date.setDate(current_date.getDate() - 7);
  const start_date = formatToSimpleDate(current_date);

  return { start_date, end_date };
};

export const getUTCLast30Days = () => {
  const current_date = new Date();
  const end_date = formatToSimpleDate(current_date);

  current_date.setMonth(current_date.getMonth() - 1);
  const start_date = formatToSimpleDate(current_date);

  return { start_date, end_date };
};

export const getUTCLastYear = () => {
  const current_date = new Date();
  const end_date = formatToSimpleDate(current_date);

  current_date.setMonth(current_date.getMonth() - 12);
  const start_date = formatToSimpleDate(current_date);

  return { start_date, end_date };
};

export const formatToSimpleDate = (date) => {
  if (!date) return null;
  return fnsFormat(new Date(date), defaultDateFormat);
};

export const isMinuteZeroOrThirty = (date) => {
  if (!date || !isValid(date)) return false;

  const minutes = getMinutes(date);
  return minutes === 0 || minutes === 30;
};

export const calculateElapsedTime = (startDate, endDate) => {
  try {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (!isValid(start) || !isValid(end)) return null;

    const diffMilliseconds = differenceInMilliseconds(end, start);
    if (diffMilliseconds < 0) return null;

    const totalSeconds = Math.floor(diffMilliseconds / 1000);
    return durationToHuman(totalSeconds);
  } catch (error) {
    return null;
  }
};

export const handleDateInputComplete = ({
  item,
  keyboardInputValue,
  inputFormat,
  userTimezone,
  onChange,
}) => {
  try {
    if (isValid(item)) {
      if (keyboardInputValue) {
        const isCompleteInput =
          keyboardInputValue.length === inputFormat.length;

        if (!isCompleteInput) {
          return;
        }
      }

      if (userTimezone) {
        const dateOnly = Boolean(inputFormat) && !/[Hhkm]/.test(inputFormat);
        return onChange(convertUserTimezoneToUTC(item, userTimezone, dateOnly));
      }

      return onChange(item);
    }
  } catch (error) {
    console.error('Error changing date:', error);
  }
};

export const defaultDateFormat = 'yyyy-MM-dd';
export const defaultDateTimeFormat = `yyyy-MM-dd HH:mm:ss`;
export const defaultMinDate = '2011-01-01T00:00:00Z';
